<script>
    import { debounce, pick } from 'lodash';
    import { DateTime } from 'luxon';
    import { onMount } from 'svelte';
    import { createForm } from 'svelte-forms-lib';
    import { getNotificationsContext } from 'svelte-notifications';
    import { boolean, number, object, string } from 'yup';

    import State from './State.svelte';
    import NextDue from './NextDue.svelte';

    import ReadOnly from 'src/fields/ReadOnly/ReadOnly.svelte';
    import Boolean from 'src/fields/ReadOnly/Boolean.svelte';
    import TextArea from 'src/fields/TextArea.svelte';
    import Number from 'src/fields/Number.svelte';
    import Button from 'src/fields/Button.svelte';
    import TimesSince from 'src/fields/TimesSince.svelte';
    import PartInformation from 'src/fields/PartInformation/PartInformation.svelte';
    import { createStateChangeHandler, updateFormStateFields } from 'src/fields/State/utils';
    import { getInitialSchema as stateInitialsSchema, getValidationSchema as stateValidationSchema } from 'src/fields/State/utils';
    import { getValidationSchema as timesSinceValidationSchema } from 'src/fields/TimesSince.svelte';
    import { getValidationSchema as partInfoValidationSchema } from 'src/fields/PartInformation/utils';

    import { complianceClient, maintenanceApi, maintenanceClient } from 'src/api';
    import { bindClass } from 'src/utils/forms';
    import { notifyError } from 'src/utils/notification';
    import { getType } from 'src/core/state';

    // Props

    export let uid;

    // Data

    const COMPLIANCE_FIELDS = [
        'compliance_date',
        'compliance_hours',
        'compliance_landings',
        'compliance_cycles',
        'end_of_month_adj',
    ];

    let url = `${uid}/`;
    let pending = true;
    let success = false;
    let isPullDueLoading = false;

    const { addNotification } = getNotificationsContext();

    let maintenance = {};

    // Form

    const {
        form,
        state,
        errors,
        isValid,
        isSubmitting,
        handleSubmit,
        updateValidateField,
    } = createForm({
        initialValues: {
            ...stateInitialsSchema(),
        },

        validationSchema: object({
            work_description: string().required(),
            man_hours: number().nullable(),

            ...stateValidationSchema('compliance_'),
            ...stateValidationSchema('next_due_', false),
            override_next_due: boolean(),
            end_of_month_adj: boolean(),

            ...partInfoValidationSchema(),
            ...timesSinceValidationSchema(),

            regular_notes: string(),
            return_to_service_notes: string(),
            admin_notes: string(),
        }),

        onSubmit: async (values) => await createCompliance(values),
    });

    // Methods

    async function fetchMaintenance() {
        try {
            pending = true;
            const { data } = await maintenanceApi(url);
            maintenance = data;
            handleSubmit();
        } catch (error) {
            notifyError({
                addNotification,
                message: 'An error occurred while fetching Non-Routine. Please contact ENG team: eng@boutiqueair.com.',
            });
        } finally {
            pending = false;
        }
    }

    async function createCompliance(values) {
        try {
            pending = true;
            const data = {
                ...values,
                maintenance: maintenance.id,
                maintenance_id: maintenance.id,
            };
            await complianceClient.create({ data });
            success = true;
        } catch(e) {
            notifyError({
                addNotification,
                message: 'An error occurred while submitting the form. Please contact ENG team: eng@boutiqueair.com.',
            });
        } finally {
            pending = false;
        }
    }

    const changeHandler = (name, val) => {
        updateValidateField(name, val);

        if (!isPullDueLoading && COMPLIANCE_FIELDS.includes(name) && !$form.override_next_due) {
            pullDue();
        }
    };

    const stateChangeHandler = createStateChangeHandler(changeHandler);
    const createChangeHandler = name => ({ detail: val }) => changeHandler(name, val);

    const pullDue = debounce(updateDue, 500);

    async function updateDue() {
        try {
            isPullDueLoading = true;

            const compliance = pick($form, COMPLIANCE_FIELDS);
            const { data } = await maintenanceClient.getCompliance({ uid: uid, data: compliance });

            Object.entries(data).forEach(([fieldName, value]) => {
                $form[fieldName] = value;
                updateValidateField(fieldName, value);
            });
        } finally {
            isPullDueLoading = false;
        }
    }

    // Events

    onMount(fetchMaintenance);

    // Reactive

    $: eqType = getType(maintenance?.type);

    $: dateInitial = DateTime.local().toISODate();
    $: hoursInitial = maintenance?.tracked_by_info?.hours;
    $: landingsInitial = maintenance?.tracked_by_info?.landings;
    $: cyclesInitial = maintenance?.tracked_by_info?.cycles;

    $: {
        updateFormStateFields($form, eqType);
    }
</script>

<svelte:head>
    <title>Add compliance</title>
</svelte:head>

<main class="mx-compliance">
    <section class="mx-compliance__header">
        <h1 class="mx-compliance__title">Add Compliance</h1>
    </section>

    {#if success}
        <p>Compliance <b>"{$form.work_description}"</b> for <b>"{maintenance.name}"</b> was successfully added.</p>
    {:else if maintenance.id}
        <section class="mx-compliance__about">
            <h3>About</h3>
            <strong>You're about to add compliance {maintenance.tracked_by_info.name}</strong>

            <div class="mx-compliance__about__fields">
                <ReadOnly label="Disposition" value={maintenance.disposition} />
                <ReadOnly label="Man Hours" value={maintenance.man_hours} />
                <ReadOnly label="Item Name" value={maintenance.name} />
                <ReadOnly label="Reference" value={maintenance.reference} />
                <ReadOnly label="Item Type" value={maintenance.type_name} />
                <ReadOnly label="Tags" value={maintenance.tags} />
                <ReadOnly label="ATA Code" value={maintenance.ata_code} />
                <Boolean label="Is Optional" value={maintenance.is_optional} />
                <ReadOnly label="Mfg Code" value={maintenance.mfg_code} />
                <Boolean label="Next Due Assist" value={maintenance.next_due_assist} />
            </div>
        </section>

        <section>
            <h3>Work Description</h3>

            <div class="field" use:bindClass={{ state, name: 'work_description' }}>
                <label class="label required" for="work-description">Notes</label>
                <TextArea
                    id="work-description"
                    bind:value={$form.work_description}
                    on:change={createChangeHandler('work_description')}
                />
            </div>

            <div class="field" use:bindClass={{ state, name: 'man_hours' }}>
                <label class="label" for="actual-work-hours">Actual Work Hours</label>
                <Number
                    id="actual-work-hours"
                    bind:value={$form.man_hours}
                    on:change={createChangeHandler('man_hours')}
                />
            </div>
        </section>

        <section class="mx-compliance__state">
            <div class="mx-compliance__state__compliance">
                <h3>Compliance State</h3>

                <State
                    equipmentType={eqType}
                    form={$form}
                    {state}

                    {dateInitial}
                    {hoursInitial}
                    {landingsInitial}
                    {cyclesInitial}

                    on:change={stateChangeHandler}
                />
            </div>

            <div class="mx-compliance__state__next-due">
                <h3>Next Due</h3>

                <NextDue
                    equipmentType={eqType}
                    form={$form}
                    {state}

                    bind:date={$form.next_due_date}
                    bind:hours={$form.next_due_hours}
                    bind:landings={$form.next_due_landings}
                    bind:cycles={$form.next_due_cycles}

                    on:change={stateChangeHandler}
                />
            </div>
        </section>

        <section>
            <h3>Part Information</h3>
            <PartInformation
                bind:partRemoved={$form.part_number_removed}
                bind:serialRemoved={$form.serial_number_removed}
                bind:removalReason={$form.removal_reason}
                bind:partInstalled={$form.part_number_installed}
                bind:serialInstalled={$form.serial_number_installed}
                bind:installStatus={$form.installed_status}
                on:change={stateChangeHandler}
            />
        </section>

        <section>
            <h3>Times Since (at Installed)</h3>
            <TimesSince
                equipmentType={eqType}

                bind:newMonths={$form.times_since_new_months}
                bind:newHours={$form.times_since_new_hours}
                bind:newLandings={$form.times_since_new_landings}
                bind:overhaulMonths={$form.times_since_overhaul_months}
                bind:overhaulHours={$form.times_since_overhaul_hours}
                bind:overhaulLandings={$form.times_since_overhaul_landings}
                bind:repairMonths={$form.times_since_repair_months}
                bind:repairHours={$form.times_since_repair_hours}
                bind:repairLandings={$form.times_since_repair_landings}
            />
        </section>

        <section>
            <h3>Notes</h3>

            <div class="field" use:bindClass={{ state, name: 'regular_notes' }}>
                <label class="label" for="regular-notes">Regular Notes</label>
                <TextArea
                    id="regular-notes"
                    bind:value={$form.regular_notes}
                    on:change={createChangeHandler('regular_notes')}
                />
            </div>

            <div class="field" use:bindClass={{ state, name: 'return_to_service_notes' }}>
                <label class="label" for="return-to-service-notes">Return To Service Notes</label>
                <TextArea
                    id="return-to-service-notes"
                    bind:value={$form.return_to_service_notes}
                    on:change={createChangeHandler('return_to_service_notes')}
                />
            </div>

            <div class="field" use:bindClass={{ state, name: 'admin_notes' }}>
                <label class="label" for="admin-notes">Admin Notes</label>
                <TextArea
                    id="admin-notes"
                    bind:value={$form.admin_notes}
                    on:change={createChangeHandler('admin_notes')}
                />
            </div>
        </section>

        <section class="mx-compliance__button">
            <Button on:click={handleSubmit} disabled="{$state.isSubmitting}">
                Confirm
            </Button>
        </section>
    {:else if pending}
        <p>Loading, please wait...</p>
    {:else}
        <div>
            <h1>Not Found</h1>
            <p>Maintenance does not exist.</p>
        </div>
    {/if}

    <section class="end-page-spacer"></section>
</main>

<style>
    .mx-compliance__about__fields {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 10px;
        margin-top: 15px;
    }

    .mx-compliance__state {
        display: flex;
        column-gap: 25px;
    }

    .mx-compliance__state__compliance {
        flex-basis: 40%;
    }

    .mx-compliance__state__next-due {
        flex-grow: 1;
    }

    .mx-compliance__button {
        margin-top: 20px;
    }

    .mx-compliance__button :global(button) {
        width: 100%;
    }
</style>
